import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  ViewChild,
} from '@angular/core';
import * as Feather from 'feather-icons';

/** Logique filtre date (jours / mois / année) partagée avec POS tickets. */
@Directive()
export abstract class PosDateFilterBase implements AfterViewInit {
  @ViewChild('datePicker') datePicker!: ElementRef;

  viewMode: 'jours' | 'mois' | 'annees' = 'jours';
  weekDays: { label: string; dateString: string; isToday: boolean }[] = [];
  currentMonthIndex = new Date().getMonth();
  currentYear = new Date().getFullYear();
  selectedMonthIndex: number | null = null;
  selectedDay = '';
  selectedYear: number | null = null;
  selectedDate: Date | null = null;
  selectedPeriod = '';
  dateFilterStart = '';
  dateFilterEnd = '';
  months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
  ];
  years: number[] = [];

  protected constructor(protected readonly cdr: ChangeDetectorRef) {}

  protected initDateFilters(): void {
    const today = new Date();
    this.selectedDate = today;
    this.selectedDay = today.toISOString().split('T')[0];
    this.selectedMonthIndex = today.getMonth();
    this.selectedYear = today.getFullYear();
    this.dateFilterStart = this.selectedDay;
    this.dateFilterEnd = this.selectedDay;
    this.updateSelectedPeriod();
    this.generateWeekDays();
    this.years = Array.from({ length: 10 }, (_, i) => this.currentYear - i).reverse();
  }

  ngAfterViewInit(): void {
    this.refreshFeatherIcons();
  }

  protected refreshFeatherIcons(): void {
    setTimeout(() => {
      try {
        Feather.replace();
      } catch {
        /* noop */
      }
      this.cdr.markForCheck();
    }, 0);
  }

  protected abstract onDateRangeChanged(): void;

  selectDay(day: { dateString: string }): void {
    this.selectedDay = day.dateString;
    this.selectedDate = new Date(day.dateString);
    this.selectedMonthIndex = this.selectedDate.getMonth();
    this.selectedYear = this.selectedDate.getFullYear();
    this.dateFilterStart = day.dateString;
    this.dateFilterEnd = day.dateString;
    this.updateSelectedPeriod();
    if (this.datePicker?.nativeElement?._flatpickr) {
      this.datePicker.nativeElement._flatpickr.setDate([day.dateString, day.dateString], false);
    }
    this.onDateRangeChanged();
  }

  selectMonth(index: number): void {
    this.selectedMonthIndex = index;
    const year = this.selectedYear !== null ? this.selectedYear : this.currentYear;
    const monthStart = new Date(year, index, 1).toISOString().split('T')[0];
    const monthEnd = new Date(year, index + 1, 0).toISOString().split('T')[0];
    this.dateFilterStart = monthStart;
    this.dateFilterEnd = monthEnd;
    this.selectedPeriod = `Mois: ${this.months[index]} ${year}`;
    this.selectedDate = new Date(year, index, 1);
    this.selectedYear = year;
    this.selectedDay = '';
    if (this.datePicker?.nativeElement?._flatpickr) {
      this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
    }
    this.onDateRangeChanged();
  }

  selectYear(year: number): void {
    this.selectedYear = year;
    if (this.viewMode === 'annees') {
      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31`;
      this.dateFilterStart = yearStart;
      this.dateFilterEnd = yearEnd;
      this.selectedPeriod = `Année: ${year}`;
      this.selectedDate = new Date(year, 0, 1);
      this.selectedMonthIndex = null;
      this.selectedDay = '';
      if (this.datePicker?.nativeElement?._flatpickr) {
        this.datePicker.nativeElement._flatpickr.setDate([yearStart, yearEnd], false);
      }
      this.onDateRangeChanged();
    } else if (this.viewMode === 'mois') {
      const monthIndex = this.selectedMonthIndex !== null ? this.selectedMonthIndex : this.currentMonthIndex;
      const monthStart = new Date(year, monthIndex, 1).toISOString().split('T')[0];
      const monthEnd = new Date(year, monthIndex + 1, 0).toISOString().split('T')[0];
      this.dateFilterStart = monthStart;
      this.dateFilterEnd = monthEnd;
      this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
      this.selectedDate = new Date(year, monthIndex, 1);
      this.selectedMonthIndex = monthIndex;
      if (this.datePicker?.nativeElement?._flatpickr) {
        this.datePicker.nativeElement._flatpickr.setDate([monthStart, monthEnd], false);
      }
      this.onDateRangeChanged();
    } else {
      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31`;
      this.dateFilterStart = yearStart;
      this.dateFilterEnd = yearEnd;
      this.selectedPeriod = `Année: ${year}`;
      this.onDateRangeChanged();
    }
  }

  incrementDate(): void {
    if (!this.selectedDate) {
      return;
    }
    if (this.viewMode === 'mois') {
      this.selectedDate.setMonth(this.selectedDate.getMonth() + 1);
      const year = this.selectedDate.getFullYear();
      const monthIndex = this.selectedDate.getMonth();
      const monthStart = new Date(year, monthIndex, 1).toISOString().split('T')[0];
      const monthEnd = new Date(year, monthIndex + 1, 0).toISOString().split('T')[0];
      this.dateFilterStart = monthStart;
      this.dateFilterEnd = monthEnd;
      this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
      this.selectedMonthIndex = monthIndex;
      this.selectedYear = year;
      this.selectedDay = '';
      this.onDateRangeChanged();
    } else if (this.viewMode === 'annees') {
      this.selectedDate.setFullYear(this.selectedDate.getFullYear() + 1);
      const year = this.selectedDate.getFullYear();
      this.dateFilterStart = `${year}-01-01`;
      this.dateFilterEnd = `${year}-12-31`;
      this.selectedPeriod = `Année: ${year}`;
      this.selectedYear = year;
      this.onDateRangeChanged();
    } else {
      this.selectedDate.setDate(this.selectedDate.getDate() + 1);
      const dayToUse = this.selectedDate.toISOString().split('T')[0];
      this.selectedDay = dayToUse;
      this.dateFilterStart = dayToUse;
      this.dateFilterEnd = dayToUse;
      this.selectedMonthIndex = this.selectedDate.getMonth();
      this.selectedYear = this.selectedDate.getFullYear();
      this.updateSelectedPeriod();
      this.onDateRangeChanged();
    }
    this.generateWeekDays();
    this.cdr.detectChanges();
  }

  decrementDate(): void {
    if (!this.selectedDate) {
      return;
    }
    if (this.viewMode === 'mois') {
      this.selectedDate.setMonth(this.selectedDate.getMonth() - 1);
      const year = this.selectedDate.getFullYear();
      const monthIndex = this.selectedDate.getMonth();
      const monthStart = new Date(year, monthIndex, 1).toISOString().split('T')[0];
      const monthEnd = new Date(year, monthIndex + 1, 0).toISOString().split('T')[0];
      this.dateFilterStart = monthStart;
      this.dateFilterEnd = monthEnd;
      this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
      this.onDateRangeChanged();
    } else if (this.viewMode === 'annees') {
      this.selectedDate.setFullYear(this.selectedDate.getFullYear() - 1);
      const year = this.selectedDate.getFullYear();
      this.dateFilterStart = `${year}-01-01`;
      this.dateFilterEnd = `${year}-12-31`;
      this.selectedPeriod = `Année: ${year}`;
      this.onDateRangeChanged();
    } else {
      this.selectedDate.setDate(this.selectedDate.getDate() - 1);
      const dayToUse = this.selectedDate.toISOString().split('T')[0];
      this.selectedDay = dayToUse;
      this.dateFilterStart = dayToUse;
      this.dateFilterEnd = dayToUse;
      this.updateSelectedPeriod();
      this.onDateRangeChanged();
    }
    this.generateWeekDays();
    this.cdr.detectChanges();
  }

  changeViewMode(mode: 'jours' | 'mois' | 'annees'): void {
    this.viewMode = mode;
    if (mode === 'jours') {
      const dayToUse = this.selectedDay || new Date().toISOString().split('T')[0];
      this.dateFilterStart = dayToUse;
      this.dateFilterEnd = dayToUse;
      this.selectedDate = new Date(dayToUse);
      this.updateSelectedPeriod();
      this.onDateRangeChanged();
    } else if (mode === 'mois') {
      const monthIndex = this.selectedMonthIndex !== null ? this.selectedMonthIndex : this.currentMonthIndex;
      const year = this.selectedYear !== null ? this.selectedYear : this.currentYear;
      const monthStart = new Date(year, monthIndex, 1).toISOString().split('T')[0];
      const monthEnd = new Date(year, monthIndex + 1, 0).toISOString().split('T')[0];
      this.dateFilterStart = monthStart;
      this.dateFilterEnd = monthEnd;
      this.selectedPeriod = `Mois: ${this.months[monthIndex]} ${year}`;
      this.onDateRangeChanged();
    } else {
      const year = this.selectedYear !== null ? this.selectedYear : this.currentYear;
      this.dateFilterStart = `${year}-01-01`;
      this.dateFilterEnd = `${year}-12-31`;
      this.selectedPeriod = `Année: ${year}`;
      this.onDateRangeChanged();
    }
  }

  openFlatpickr(): void {
    if (this.datePicker?.nativeElement?._flatpickr) {
      this.datePicker.nativeElement._flatpickr.open();
    }
  }

  protected updateSelectedPeriod(): void {
    const dateToUse = this.selectedDate || new Date();
    if (this.viewMode === 'mois' && this.selectedMonthIndex !== null) {
      const selectedYear = this.selectedYear || dateToUse.getFullYear();
      this.selectedPeriod = `Mois: ${this.months[this.selectedMonthIndex]} ${selectedYear}`;
    } else if (this.viewMode === 'annees' && this.selectedYear !== null) {
      this.selectedPeriod = `Année: ${this.selectedYear}`;
    } else {
      this.selectedPeriod = `Jour: ${dateToUse.getDate()} ${this.months[dateToUse.getMonth()]} ${dateToUse.getFullYear()}`;
    }
  }

  protected generateWeekDays(): void {
    const today = new Date();
    const startOfWeek = new Date(this.selectedDate || today);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);
    this.weekDays = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return {
        label: d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' }),
        dateString: d.toISOString().split('T')[0],
        isToday: d.toDateString() === today.toDateString(),
      };
    });
  }
}
